/**
 * Быстрый переход (Ctrl/⌘+K): поиск по серверам, каналам, беседам и друзьям. Здесь только
 * разбор запроса и ранжирование — без сторов, их проверяет web/scripts/navigation.test.mjs.
 * Поиск идёт по заранее подготовленным строкам: на сотнях каналов нажатие не должно тормозить.
 */

import type { AvatarDecoration, User } from '@/types/api'

export type PlaceKind = 'guild' | 'text' | 'forum' | 'voice' | 'dm' | 'group' | 'friend'

export interface Place {
  /** g:<сервер>, c:<канал сервера>, d:<беседа>, f:<друг без открытой беседы>. */
  key: string
  kind: PlaceKind
  name: string
  /** Сервер канала, @имя собеседника, число участников группы. */
  sub?: string | undefined
  /** Что ещё ищется, кроме названия: имена пользователей. */
  terms?: string[] | undefined
  guildId?: string | undefined
  channelId?: string | undefined
  userId?: string | undefined
  mentions?: number | undefined
  unread?: boolean | undefined
  /** Картинка: иконка сервера или аватар собеседника; id — для цвета заглушки. */
  image?: string | undefined
  imageId?: string | undefined
  /** Рамка аватара собеседника или друга. */
  decoration?: AvatarDecoration | null | undefined
  /** Собеседники группы — для двух аватарок внахлёст. */
  recipients?: User[] | undefined
}

/** Префиксы сужают поиск, как в Discord: @ — люди и беседы, # — текстовые каналы и форумы, ! — голосовые, * — серверы. */
const PREFIXES: Record<string, PlaceKind[]> = {
  '@': ['dm', 'group', 'friend'],
  '#': ['text', 'forum'],
  '!': ['voice'],
  '*': ['guild'],
}

export function parseQuery(raw: string): { text: string; kinds: PlaceKind[] | null } {
  const q = raw.trimStart()
  const kinds = PREFIXES[q.charAt(0)]
  return kinds ? { text: normalize(q.slice(1)), kinds } : { text: normalize(q), kinds: null }
}

export function normalize(s: string): string {
  return s.trim().toLowerCase().replace(/ё/g, 'е')
}

/**
 * Насколько название подходит под запрос: совпало целиком > начало > начало слова > подстрока >
 * буквы по порядку («прдж» → «продажи»). 0 — не подходит. Короткое название при равенстве выше.
 */
export function score(query: string, name: string): number {
  if (!query) return 1
  if (!name) return 0
  if (name === query) return 1000
  if (name.startsWith(query)) return 800 - Math.min(100, name.length - query.length)
  const at = name.indexOf(query)
  if (at > 0) {
    const before = name.charAt(at - 1)
    if (before === ' ' || before === '-' || before === '_' || before === '.' || before === '/') return 600 - Math.min(100, at)
    return 400 - Math.min(100, at)
  }
  // буквы запроса по порядку, штраф за каждый пропуск; для одной-двух букв это шум, а не поиск
  if (query.length < 3) return 0
  let gaps = 0
  let i = 0
  for (let j = 0; j < name.length && i < query.length; j++) {
    if (name.charAt(j) === query.charAt(i)) i++
    else if (i > 0) gaps++
  }
  if (i < query.length) return 0
  return Math.max(1, 200 - gaps * 5)
}

/** Подготовленное место: названия уже в нижнем регистре, чтобы не считать их на каждое нажатие. */
export interface Indexed {
  place: Place
  name: string
  terms: string[]
}

export function indexPlaces(places: Place[]): Indexed[] {
  return places.map((place) => ({ place, name: normalize(place.name), terms: (place.terms ?? []).map(normalize) }))
}

/** Порядок видов при равных очках: сначала люди и беседы, потом каналы, потом серверы. */
const KIND_ORDER: Record<PlaceKind, number> = { dm: 0, group: 1, friend: 2, text: 3, forum: 4, voice: 5, guild: 6 }

/**
 * Результаты поиска. Пустой запрос — недавние места, за ними места с упоминаниями;
 * иначе — по очкам, с прибавкой недавним.
 */
export function searchPlaces(index: Indexed[], raw: string, recent: string[], limit = 50): Place[] {
  const { text, kinds } = parseQuery(raw)
  const pool = kinds ? index.filter((x) => kinds.includes(x.place.kind)) : index
  const recency = new Map(recent.map((key, i) => [key, i]))
  if (!text) {
    const byKey = new Map(pool.map((x) => [x.place.key, x.place]))
    const out: Place[] = []
    const seen = new Set<string>()
    for (const key of recent) {
      const p = byKey.get(key)
      if (p && !seen.has(key)) {
        out.push(p)
        seen.add(key)
      }
    }
    const loud = pool
      .filter((x) => !seen.has(x.place.key) && (x.place.mentions ?? 0) > 0)
      .sort((a, b) => (b.place.mentions ?? 0) - (a.place.mentions ?? 0))
    for (const x of loud) {
      out.push(x.place)
      seen.add(x.place.key)
    }
    // На первом открытии без истории предлагаем беседы и друзей.
    if (!kinds) out.push(...pool.filter((x) => !seen.has(x.place.key) && ['dm', 'group', 'friend'].includes(x.place.kind)).map((x) => x.place).slice(0, 8))
    // С одним префиксом («#») добавляем все остальные места этого вида по алфавиту.
    if (kinds) out.push(...pool.filter((x) => !seen.has(x.place.key)).map((x) => x.place).sort((a, b) => a.name.localeCompare(b.name, 'ru')))
    return out.slice(0, limit)
  }
  const scored: { place: Place; score: number }[] = []
  for (const x of pool) {
    let best = score(text, x.name)
    for (const t of x.terms) best = Math.max(best, score(text, t) - 50)
    if (best <= 0) continue
    const r = recency.get(x.place.key)
    scored.push({ place: x.place, score: best + (r === undefined ? 0 : 60 - Math.min(r, 20) * 2) })
  }
  scored.sort((a, b) => b.score - a.score || KIND_ORDER[a.place.kind] - KIND_ORDER[b.place.kind] || a.place.name.localeCompare(b.place.name, 'ru'))
  return scored.slice(0, limit).map((s) => s.place)
}

/** Недавние места: самое свежее первым, без повторов, не длиннее max. */
export function pushRecent(list: string[], key: string, max = 20): string[] {
  return [key, ...list.filter((k) => k !== key)].slice(0, max)
}

/** Разделы без дубликатов: ранжирование внутри каждого сохраняется. */
export function groupPlaces(places: Place[], raw: string, recent: string[]): { label: string; places: Place[] }[] {
  if (raw.trim()) return [{ label: 'Результаты поиска', places }]
  const recentKeys = new Set(recent)
  const groups = [
    { label: 'Недавние переходы', places: places.filter((p) => recentKeys.has(p.key)) },
    { label: 'Упоминания', places: places.filter((p) => !recentKeys.has(p.key) && (p.mentions ?? 0) > 0) },
    { label: 'Беседы и друзья', places: places.filter((p) => !recentKeys.has(p.key) && !(p.mentions ?? 0)) },
  ]
  return groups.filter((group) => group.places.length > 0)
}
